<script>
import Settings from '@/components/profile/administrationManagement/settings/AdministrationManagementSettings.vue'
import ChipPermissions from '@/components/backoffice/organization/ChipPermissions.vue'
import UserManagementTable from '@/components/backoffice/organization/UserManagementTable.vue'
import AdministrationManagementTableAction
  from '@/components/profile/administrationManagement/AdministrationManagementTableAction.vue'
import OrganizationAddress from '@/components/backoffice/organization/OrganizationAddress.vue'
import { mapActions } from 'vuex'

export default {
  components: {
    OrganizationAddress,
    UserManagementTable,
    ChipPermissions,
    Settings,
    DialogAction: AdministrationManagementTableAction
  },
  props: {
    //organization view response
    value: {
      type: Array
    },
    organizations: {
      type: Array
    },
    loading: {
      type: Boolean
    },
    //role view response
    roles: {
      type: Array
    }
  },
  data() {
    return {
      drawer: { state: false, data: {} },
      selectedOrganization: {},
      userSettingsToggle: { state: false, item: {} },
      search: '',
      credentials: { roles: [] },
      updateOrganization: false,
      organizationName: '',
      isSubOrganisation: false,
      selectedSubOrganisationId: null
    }
  },
  computed: {
    flattenedOrganizations() {
      return this.organizations.reduce((acc, val) => {
        acc.push(val)
        if (val.children) {
          acc = acc.concat(val.children)
        }
        return acc
      }, [])
    },
    treeviewOrganizations() {
      let organizations = this.$props.organizations

      if (this.search) {
        organizations = organizations?.filter(org => {
          const searchIsNumber = !isNaN(Number(this.search));

          return searchIsNumber
            ? org.id === Number(this.search)
            : org.name?.toLowerCase().includes(this.search.toLowerCase());
        });
      }

      return organizations = organizations.sort((a, b) =>
        a.name?.localeCompare(b.name))
    }
  },
  mounted() {
    if (this.$props.organizations.length > 0) {
      this.selectedOrganization = this.treeviewOrganizations[0]
    }
  },
  methods: {
    ...mapActions(['createOrganization']),
    handleUserSettingsToggle(event) {
      this.userSettingsToggle = event
    },
    pickOrganization(selected) {
      this.selectedOrganization = selected[0]
      this.$emit('pick-organization', selected)
    },
    renameAndPersist() {
      this.updateOrganization = false
      this.$organizationService.rename(this.selectedOrganization)
      this.$emit('update-organization', this.selectedOrganization)
    },
    resetOrganizationCreationFields() {
      this.organizationName = ''
      this.isSubOrganisation = false
      this.selectedSubOrganisationId = null
    },
    createNewOrganization() {
      const organization = {
        name: this.organizationName,
        parentOrganizationId: this.isSubOrganisation ? this.selectedSubOrganisationId : null,
        inheritPermissions: true
      }
      this.createOrganization(organization)
      this.$emit('create-organization', organization)
      this.resetOrganizationCreationFields()
    }
  }
}
</script>
<template>
  <div>
    <Settings
      v-model="userSettingsToggle"
      :selected-organization-from-backoffice="selectedOrganization" />
    <v-sheet class="overflow-hidden" style="position: relative;">
      <v-row>
        <v-col cols="2">
          <v-sheet class="pb-2">
            <v-row>
              <v-col cols="10">
                <v-text-field
                  v-model="search"
                  :label="$t('component.backoffice.organizationManagement.search.organization')"
                  flat
                  hide-details
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  clear-icon="mdi-close-circle-outline"
                ></v-text-field>
              </v-col>
              <v-col cols="2" class="align-content-end">
                <DialogAction
                  confirmation
                  icon="mdi-plus"
                  @confirmed="createNewOrganization"
                  @canceled="resetOrganizationCreationFields">
                  <template slot="confirmationContent">
                    <div v-html="$t('component.backoffice.organizationManagement.name.organization')"></div>
                    <v-text-field
                      v-model="organizationName"
                      :label="$t('component.backoffice.organizationManagement.name.of.organization')"
                      flat
                      hide-details
                      prepend-inner-icon="mdi-domain"
                      clearable
                    ></v-text-field>
                    <v-checkbox
                      v-model="isSubOrganisation"
                      :label="$t('component.backoffice.organizationManagement.suborganization')"></v-checkbox>
                    <v-autocomplete
                      v-if="isSubOrganisation"
                      v-model="selectedSubOrganisationId"
                      :flat="false"
                      :items="flattenedOrganizations"
                      :item-text="item => item.name"
                      :item-value="item => item.id"
                      :label="$t('component.backoffice.organizationManagement.select')"
                      :no-data-text="$t('global.no.data.text')"
                      hide-details
                      density="compact"
                      clearable>
                      <template #item="{ item }">
                        <small>{{item.id}}:&nbsp;</small>
                        <small>{{item.name}}</small>
                      </template>
                    </v-autocomplete>
                  </template>
                  <template slot="tooltip">
              <span
                v-html="$t('component.backoffice.organizationManagement.create.new.organization')">
                </span>
                  </template>
                </DialogAction>
              </v-col>
            </v-row>
          </v-sheet>
          <v-treeview
            :items="treeviewOrganizations"
            activatable
            selection-type="independent"
            dense
            active-class="isActive"
            return-object
            item-key="id"
            @update:active="pickOrganization">
            <template #prepend="{ open }">
              <v-icon>
                {{ open ? 'mdi-folder-open' : 'mdi-folder' }}
              </v-icon>
            </template>
            <template #label="{item}">
              <span
                :class="selectedOrganization.id && selectedOrganization.id === item.id ? 'isActive' : ''"
                :title="item.id">
                <small class="organization-id-prefix">{{item.id}}:</small> {{ item.name }}
              </span>
            </template>
          </v-treeview>
        </v-col>
        <v-col cols="10">
          <div class="mt-2">
            <div v-if="selectedOrganization.name">
              <small v-if="selectedOrganization.aplId">#{{ selectedOrganization.aplId }}</small>
              <div v-if="!updateOrganization">
                <h2 class="mt-1">{{ selectedOrganization.name }}
                  <v-icon class="mb-2" @click.prevent.stop="updateOrganization = !updateOrganization">mdi-pencil
                  </v-icon>
                </h2>
              </div>
              <v-row v-else no-gutters>
                <v-col cols="8">
                  <v-text-field
                    id="organizationNameUpdater"
                    v-model="selectedOrganization.name"
                    :label="$t('component.backoffice.organizationManagement.organization.name.label')"
                    outlined
                    dense
                    required />
                </v-col>
                <v-col cols="4">
                  <v-btn
                    color="primary"
                    class="ms-1 me-1"
                    @click="renameAndPersist">{{$t('button.confirm')}}
                  </v-btn>
                  <v-btn @click="updateOrganization = false">{{$t('button.cancel')}}</v-btn>
                </v-col>
              </v-row>
            </div>
            <br>
            <OrganizationAddress
              :selected-organization="selectedOrganization"
              :update-organization="updateOrganization" />
            <div>
              <ChipPermissions
                v-if="selectedOrganization.permissions"
                class="mb-10 mt-10"
                :selected-organization="selectedOrganization"
                :items="selectedOrganization.permissions" />
              <UserManagementTable
                v-if="selectedOrganization.users"
                :organization="selectedOrganization"
                :employees="selectedOrganization.users"
                @onUserSettingsToggle="handleUserSettingsToggle" />
              <div v-else class="text-center">
                <h2 class="mt-15">{{$t('component.backoffice.organizationManagement.select.organization')}}</h2>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-sheet>
  </div>
</template>
<style scoped>
.isActive {
  font-weight: 800;
}
.organization-id-prefix{
  font-size:12px;
  font-weight:300;
}
</style>
